import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

const PERMISSION_LABELS = {
  can_approve: { en: 'Approve KYC', zh: '审核通过KYC' },
  can_reject: { en: 'Reject KYC', zh: '拒绝KYC' },
  can_set_risk: { en: 'Set Risk Level', zh: '设置风险等级' },
  can_add_notes: { en: 'Add Notes', zh: '添加备注' },
  can_view_files: { en: 'View Files', zh: '查看文件' },
  can_suspend: { en: 'Suspend Accounts', zh: '封禁账户' },
  can_upload: { en: 'Upload Documents', zh: '上传文件' },
  can_manage_admins: { en: 'Manage Admins', zh: '管理管理员' },
}

const DEFAULT_PERMISSIONS = {
  can_approve: false, can_reject: false, can_set_risk: false,
  can_add_notes: false, can_view_files: false, can_suspend: false,
  can_upload: false, can_manage_admins: false,
}

export default function AdminManagement({ admin, lang }) {
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', role: 'reviewer', permissions: { ...DEFAULT_PERMISSIONS } })

  const t = (key) => PERMISSION_LABELS[key]?.[lang] || key
  const tl = (en, zh) => lang === 'zh' ? zh : en

  useEffect(() => { fetchAdmins() }, [])

  const fetchAdmins = async () => {
    setLoading(true)
    const { data } = await supabase.from('admin_users').select('*').order('created_at')
    setAdmins(data || [])
    setLoading(false)
  }

  const handleAdd = async () => {
    if (!newAdmin.email || !newAdmin.name) { alert(tl('Please fill email and name', '请填写邮箱和姓名')); return }
    const { error } = await supabase.from('admin_users').insert({
      email: newAdmin.email.toLowerCase().trim(),
      name: newAdmin.name,
      role: newAdmin.role,
      permissions: newAdmin.permissions,
    })
    if (error) { alert(`Error: ${error.message}`); return }
    setShowAdd(false)
    setNewAdmin({ email: '', name: '', role: 'reviewer', permissions: { ...DEFAULT_PERMISSIONS } })
    fetchAdmins()
  }

  const handleUpdatePermissions = async (adminId, permissions) => {
    await supabase.from('admin_users').update({ permissions }).eq('id', adminId)
    fetchAdmins()
    setEditingId(null)
  }

  const handleToggleActive = async (adminId, currentStatus) => {
    if (adminId === admin.id) { alert(tl('Cannot deactivate your own account', '不能停用自己的账号')); return }
    await supabase.from('admin_users').update({ is_active: !currentStatus }).eq('id', adminId)
    fetchAdmins()
  }

  const PermissionEditor = ({ permissions, onChange }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
      {Object.keys(DEFAULT_PERMISSIONS).map(key => (
        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '6px 8px', background: permissions[key] ? '#f0fdf4' : '#f9fafb', borderRadius: '6px', border: `1px solid ${permissions[key] ? '#86efac' : '#e5e7eb'}` }}>
          <input type="checkbox" checked={!!permissions[key]}
            onChange={e => onChange({ ...permissions, [key]: e.target.checked })} />
          <span style={{ color: permissions[key] ? '#166534' : '#6b7280' }}>{t(key)}</span>
        </label>
      ))}
    </div>
  )

  if (!admin.permissions?.can_manage_admins) {
    return (
      <div style={{ background: '#fff', borderRadius: '10px', padding: '40px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>{tl('You do not have permission to manage admins', '您没有权限管理管理员账号')}</p>
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{tl('Admin Management', '管理员管理')}</h2>
        <button onClick={() => setShowAdd(true)}
          style={{ padding: '8px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}>
          + {tl('Add Admin', '添加管理员')}
        </button>
      </div>

      {/* 添加管理员表单 */}
      {showAdd && (
        <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>{tl('New Admin Account', '新增管理员账号')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>Email *</label>
              <input value={newAdmin.email} onChange={e => setNewAdmin(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@finconnectx.com"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>{tl('Name', '姓名')} *</label>
              <input value={newAdmin.name} onChange={e => setNewAdmin(p => ({ ...p, name: e.target.value }))}
                placeholder={tl('Full name', '姓名')}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', color: '#374151', display: 'block', marginBottom: '4px' }}>{tl('Role', '角色')}</label>
            <select value={newAdmin.role} onChange={e => setNewAdmin(p => ({ ...p, role: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', background: '#fff' }}>
              <option value="reviewer">Reviewer</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>{tl('Permissions', '权限配置')}</label>
            <PermissionEditor permissions={newAdmin.permissions} onChange={p => setNewAdmin(prev => ({ ...prev, permissions: p }))} />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleAdd}
              style={{ padding: '8px 20px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
              {tl('Save', '保存')}
            </button>
            <button onClick={() => setShowAdd(false)}
              style={{ padding: '8px 20px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
              {tl('Cancel', '取消')}
            </button>
          </div>
        </div>
      )}

      {/* 管理员列表 */}
      <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : admins.map((a, i) => (
          <div key={a.id} style={{ padding: '16px 20px', borderBottom: i < admins.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{a.name}</span>
                  <span style={{ background: a.role === 'super_admin' ? '#fef3c7' : a.role === 'admin' ? '#eff6ff' : '#f3f4f6', color: a.role === 'super_admin' ? '#92400e' : a.role === 'admin' ? '#1d4ed8' : '#374151', padding: '1px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
                    {a.role}
                  </span>
                  <span style={{ background: a.is_active ? '#f0fdf4' : '#fef2f2', color: a.is_active ? '#166534' : '#991b1b', padding: '1px 8px', borderRadius: '10px', fontSize: '11px' }}>
                    {a.is_active ? tl('Active', '启用') : tl('Inactive', '停用')}
                  </span>
                  {a.id === admin.id && (
                    <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px', borderRadius: '10px', fontSize: '11px' }}>
                      {tl('You', '当前账号')}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>{a.email}</p>
                {a.last_login_at && (
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0' }}>
                    {tl('Last login', '最后登录')}: {new Date(a.last_login_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setEditingId(editingId === a.id ? null : a.id)}
                  style={{ padding: '5px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                  {editingId === a.id ? tl('Cancel', '取消') : tl('Edit Permissions', '编辑权限')}
                </button>
                {a.id !== admin.id && (
                  <button onClick={() => handleToggleActive(a.id, a.is_active)}
                    style={{ padding: '5px 12px', background: a.is_active ? '#fef2f2' : '#f0fdf4', color: a.is_active ? '#dc2626' : '#16a34a', border: `1px solid ${a.is_active ? '#fecaca' : '#86efac'}`, borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    {a.is_active ? tl('Deactivate', '停用') : tl('Activate', '启用')}
                  </button>
                )}
              </div>
            </div>

            {/* 权限编辑 */}
            {editingId === a.id && (
              <div style={{ marginTop: '14px', padding: '14px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>{tl('Edit Permissions', '编辑权限')}</p>
                <PermissionEditor
                  permissions={a.permissions || DEFAULT_PERMISSIONS}
                  onChange={p => setAdmins(prev => prev.map(x => x.id === a.id ? { ...x, permissions: p } : x))} />
                <button onClick={() => handleUpdatePermissions(a.id, admins.find(x => x.id === a.id).permissions)}
                  style={{ marginTop: '10px', padding: '7px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                  {tl('Save Permissions', '保存权限')}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}